import {inject, Injectable} from '@angular/core';
import {Router, Scroll} from '@angular/router';
import {ViewportScroller} from '@angular/common';
import {filter} from 'rxjs';
import {isDefined} from '@aviary/store/common/typing.helpers';
import {SCROLL_RESTORATION_FALLBACK} from '@aviary/injection-tokens/app-provided.tokens';

export interface ScrollRestorationFallback {
  takePendingPosition(): [number, number] | undefined;
}

interface PendingScrollPosition {
  readonly navigationId: number;
  readonly position: [number, number];
}

/**
 * This service is responsible for intercepting the scroll position from the Router (thanks to the scrollPositionRestoration setting), and restoring it
 * AFTER our loading indicator dialog is dismissed, which signals that the data on the page is loaded and ready. It solves a timing problem because our
 * loading indicator dialog's scroll lock prevents the Router from applying the saved scroll position.
 *
 * - Any back or forward navigation event from the browser's history will restore the user's previous scroll position. (Purposefully ignoring tabbed routes.)
 * - Any in-app navigation will appropriately set the scroll position to the top of the page.
 */

@Injectable({providedIn: 'root'})
export class ScrollRestorationService {
  private readonly router = inject(Router);
  private readonly viewportScroller = inject(ViewportScroller);
  private readonly restorationFallback = inject(SCROLL_RESTORATION_FALLBACK, {optional: true});
  private pendingScroll: PendingScrollPosition | undefined;
  private loadedNavigationId: number | undefined;

  constructor() {
    this.router.events.pipe(
      filter((event): event is Scroll => event instanceof Scroll),
    ).subscribe(event => {
      const navigationId: number = event.routerEvent.id;
      if (navigationId !== this.currentNavigationId || navigationId === this.pendingScroll?.navigationId) return;

      // Repeat scroll position restoration OR scroll to top when signaled by a loading dialog being dismissed.
      this.pendingScroll = {navigationId, position: event.position ?? [0, 0]};
      this.restorePendingScroll();
    });
  }

  /**
   * Called when loading finishes and the indicator's dialog has closed. This allows us to accurately restore the user's past scroll position on long lists
   * or pages with lots of data when they use the browser nav buttons, even with a long loading time.
   *
   * - If we were given a specific scroll position from the store as an override (e.g., a Hawk search page), restore that position.
   * - If the related navigation event was a browser back/forward navigation event, restore the router's saved scroll position.
   * - If we were told to re-scroll the viewport to the top, do that.
   */
  public restoreScrollWhenLoaded(navigationId: number | undefined): void {
    if (!isDefined(navigationId) || navigationId !== this.currentNavigationId) return;

    // Loading can finish before Angular emits the navigation's deferred Scroll event.
    this.loadedNavigationId = navigationId;
    this.restorePendingScroll();
  }

  private get currentNavigationId(): number | undefined {
    return this.router.currentNavigation()?.id ?? this.router.lastSuccessfulNavigation?.id;
  }

  private restorePendingScroll(): void {
    const pendingScroll: PendingScrollPosition | undefined = this.pendingScroll;
    if (!isDefined(pendingScroll) || pendingScroll.navigationId !== this.loadedNavigationId) return;

    requestAnimationFrame(() => {
      // Check ownership before consuming either the router position or fallback, including same-URL navigations.
      if (pendingScroll.navigationId !== this.currentNavigationId || this.pendingScroll !== pendingScroll) return;

      const position: [number, number] = this.restorationFallback?.takePendingPosition() ?? pendingScroll.position;
      this.pendingScroll = undefined;
      this.loadedNavigationId = undefined;
      this.viewportScroller.scrollToPosition(position);
    });
  }
}
